/**
 * Shaping behind the provider profile's tape: the 30 daily readings regrouped
 * by platform so the /usage chart can draw them, and one ranked row per
 * endpoint joining the provider's traffic with its catalog record.
 *
 * Pure. The day buckets come from `providerTrafficDays`, the one owner of how a
 * provider's traffic points fall into UTC days.
 */

import type { NetworkDay } from '../../data/analytics';
import type { ProviderTrafficPoint } from '../../data/providerTraffic';
import type { CatalogApi, CategoryId } from '../../features/catalog/model';
import type { ProviderEndpoint } from '../../features/catalog/providerProfiles';
import { platformInfoOf } from '../../lib/catalogMap';
import { platformKey } from '../../lib/catalogPlatform';
import { quotedRateUsd, type DisplayPrice } from '../../lib/pricing';
import { providerTrafficDays, type TrafficDay } from './providerTrafficSeries';

export interface ProviderTapeRow {
  sku: string;
  /** traffic rank, fixed by requests so it survives a re-sort */
  rank: number;
  name: string;
  href: string;
  platformId: string;
  icon: string;
  category: CategoryId;
  /** requests per day over the window, oldest first */
  daily: number[];
  requests: number;
  successPct: number | null;
  p50Ms: number | null;
  price: DisplayPrice;
}

export type ProviderTapeKey = 'rank' | 'name' | 'daily' | 'requests' | 'success' | 'p50' | 'price';

export interface ProviderTapeSort {
  key: ProviderTapeKey;
  dir: 'asc' | 'desc';
}

/** The provider's days in the shape the network chart reads: requests keyed by
 *  catalog platform, so a platform is drawn in the same band it holds on /usage. */
export const providerNetworkDays = (days: TrafficDay[]): NetworkDay[] =>
  days.map((day) => {
    const platforms: Record<string, number> = {};
    for (const segment of day.segments) {
      const id = platformKey(segment.sku);
      platforms[id] = (platforms[id] ?? 0) + segment.requests;
    }
    return { date: day.key, requests: day.total, platforms };
  });

/** One row per endpoint the provider stands behind. With no traffic yet, the
 *  catalog's own served count stands in and the daily series is flat. */
export const buildProviderTapeRows = (
  endpoints: ProviderEndpoint[],
  points: ProviderTrafficPoint[] | undefined,
  cards: CatalogApi[],
): ProviderTapeRow[] => {
  const days = providerTrafficDays(points ?? []);
  const byCard = new Map(cards.map((card) => [card.id, card]));
  return endpoints
    .map((endpoint) => {
      const meta = platformInfoOf(endpoint.sku);
      const card = byCard.get(meta.id);
      const daily = days.map(
        (day) => day.segments.find((segment) => segment.sku === endpoint.sku)?.requests ?? 0,
      );
      return {
        sku: endpoint.sku,
        name: endpoint.name,
        href: endpoint.href,
        platformId: meta.id,
        icon: card?.icon ?? meta.icon,
        category: card?.category ?? 'data',
        daily,
        requests: points ? daily.reduce((sum, n) => sum + n, 0) : endpoint.requests,
        successPct: endpoint.uptimePct ?? null,
        p50Ms: endpoint.latencyMs ?? null,
        price: endpoint.price,
      };
    })
    .sort((a, b) => b.requests - a.requests || a.name.localeCompare(b.name))
    .map((row, index) => ({ ...row, rank: index + 1 }));
};

/** Unmeasured always sinks, whichever way the column is pointing. */
const compare = (a: number | null, b: number | null, dir: 1 | -1): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * dir;
};

const VALUE: Record<ProviderTapeKey, (row: ProviderTapeRow) => number | null> = {
  rank: (row) => row.rank,
  name: () => null,
  daily: (row) => row.requests,
  requests: (row) => row.requests,
  success: (row) => row.successPct,
  p50: (row) => row.p50Ms,
  price: (row) => quotedRateUsd(row.price),
};

export const sortProviderTapeRows = (
  rows: ProviderTapeRow[],
  sort: ProviderTapeSort,
): ProviderTapeRow[] => {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.key === 'name') return a.name.localeCompare(b.name) * dir;
    const byValue = compare(VALUE[sort.key](a), VALUE[sort.key](b), dir);
    return byValue !== 0 ? byValue : a.rank - b.rank;
  });
};
